import type { MutableRefObject, ReactNode } from 'react';
import { Box } from '@mui/material';
import { BottomSheet } from 'react-spring-bottom-sheet';
import type { RefHandles } from 'react-spring-bottom-sheet/dist/types';
import { makeStyles } from 'tss-react/mui';
import { ForcedTheme } from 'components/ForcedTheme';
import { DARK_SURFACE, darkPalette } from 'theme/palette';

type TimelineSheetProps = {
  sheetRef: MutableRefObject<RefHandles | null>;
  /** Always visible; the sheet's collapsed height */
  header: ReactNode;
  /** Revealed by pulling the sheet up */
  children?: ReactNode;
  /** When false, pinned to the bottom and only the header is shown. */
  expandable: boolean;
};

const useStyles = makeStyles<{ expandable: boolean }>()(
  (theme, { expandable }) => ({
    sheet: {
      // Styling for the sheet contents
      '--rsbs-bg': expandable
        ? darkPalette.background?.secondary
        : DARK_SURFACE,
      // Styling for the sheet header
      '& [data-rsbs-header]': {
        backgroundColor: DARK_SURFACE,
        borderTopLeftRadius: 'var(--rsbs-overlay-rounded, 16px)',
        borderTopRightRadius: 'var(--rsbs-overlay-rounded, 16px)',
        padding: 0,
        paddingTop: 8,
        textAlign: 'left',
        // Styling for the sheet handle
        '&:before': {
          top: 6,
          backgroundColor: theme.palette.alert.neutral.background,
        },
      },
    },
  }),
);

export const collapseTimelineSheet = (
  sheetRef: MutableRefObject<RefHandles | null>,
): void => {
  sheetRef.current?.snapTo(({ snapPoints }) => Math.min(...snapPoints));
};

/**
 * Wraps the given header and children in a {@link BottomSheet}.
 *
 * The header is always visible. When `expandable` is `false` any children will
 * be hidden and the sheet will be locked in place.
 */
export const TimelineSheet = ({
  sheetRef,
  header,
  children,
  expandable,
}: TimelineSheetProps): JSX.Element => {
  const { classes } = useStyles({ expandable });

  return (
    <BottomSheet
      open
      ref={sheetRef}
      blocking={false}
      expandOnContentDrag={expandable}
      className={classes.sheet}
      // The sheet only opens once its content has a height. So, when not
      // expandable, swap the header into the content area.
      header={expandable && <ForcedTheme mode="dark">{header}</ForcedTheme>}
      snapPoints={({ headerHeight, minHeight }) =>
        expandable ? [headerHeight, minHeight] : [minHeight]
      }
      defaultSnap={({ snapPoints }) => Math.max(...snapPoints)}
    >
      <ForcedTheme mode="dark">
        {expandable ? children : <Box sx={{ pt: 1 }}>{header}</Box>}
      </ForcedTheme>
    </BottomSheet>
  );
};
